import React from 'react'

import { Box, Button, Icon } from '@factorialco/deprecated-design-system'
import { AnimatePresence, Reorder } from 'framer-motion'
import { v4 as uuid } from 'uuid'

import TextArea from 'modules/core/components/TextArea'

import {
  MultipleChoiceQuestion,
  SingleChoiceQuestion,
  Translations,
} from 'modules/questionnaires/lib/types'

type QuestionChoice = SingleChoiceQuestion | MultipleChoiceQuestion
type IdChoice = {
  id: string
  value: string
}

type Props = {
  choices: QuestionChoice['choiceOptions']
  translations: Translations['question']['multiple_choice_configurator']
  errors?: string[] | string | undefined
  disabled?: boolean
  onChange: (choices: QuestionChoice['choiceOptions']) => void
}

const MultipleChoiceConfiguration: React.FC<Props> = ({
  choices = [],
  translations,
  errors,
  disabled = false,
  onChange,
}) => {
  // The id is needed to make stable comparisons with the Reorder component
  // using the index of the array, due to the array manipulation cause the reorder glitch
  // if using the text the TextFields will be re-rendered and the focus will be lost
  const identifiedChoices: IdChoice[] = choices.map((choice) => ({
    id: uuid(),
    value: choice,
  }))

  const [orderedChoices, setOrderedChoices] = React.useState(identifiedChoices)

  const handleChange = (changedChoices: IdChoice[]) => {
    onChange(changedChoices.map((choice) => choice.value))
  }

  const handleReorder = (reordered: typeof identifiedChoices) => {
    setOrderedChoices(reordered)
    handleChange(reordered)
  }

  const addChoice = () => {
    const newChoices = [...orderedChoices, { id: uuid(), value: '' }]
    setOrderedChoices(newChoices)
    handleChange(newChoices)
  }

  const deleteChoice = (id: string) => {
    const newChoices = orderedChoices.filter((choice) => choice.id !== id)
    setOrderedChoices(newChoices)
    handleChange(newChoices)
  }

  const updateChoice = (value: string, id: string) => {
    const newChoices = orderedChoices.map((choice) =>
      choice.id === id ? { ...choice, value } : choice
    )
    setOrderedChoices(newChoices)
    handleChange(newChoices)
  }

  return (
    <Box flexDirection="column" gap="s16">
      <Reorder.Group values={orderedChoices} onReorder={handleReorder} initial={false}>
        <Box flexDirection="column" gap="s16">
          <AnimatePresence mode="popLayout" initial={false}>
            {orderedChoices.map((choice, i) => {
              const choiceError = Array.isArray(errors) ? errors[0] : undefined
              return (
                <Reorder.Item
                  as="div"
                  key={choice.id}
                  value={choice}
                  initial={{ opacity: 0, y: 60 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: 300 }}
                >
                  <Box flexDirection="row" alignItems="center" gap="s8">
                    <Box cursor="pointer">
                      <Icon.Medium icon="drag-and-drop" color="grey800" />
                    </Box>
                    <Box flexGrow flexDirection="row">
                      <TextArea
                        mandatory
                        error={!choice.value.length ? choiceError : undefined}
                        value={choice.value}
                        placeholder={`${translations.placeholder} ${i + 1}`}
                        onChange={(value) => updateChoice(value, choice.id)}
                        readonly={disabled}
                      />
                    </Box>
                    <Button
                      type="secondary"
                      iconName="delete"
                      onClick={() => deleteChoice(choice.id)}
                      disabled={disabled}
                    />
                  </Box>
                </Reorder.Item>
              )
            })}
          </AnimatePresence>
        </Box>
      </Reorder.Group>
      <Button type="secondary" iconName="add" onClick={() => addChoice()}>
        {translations.add_option}
      </Button>
    </Box>
  )
}

export default MultipleChoiceConfiguration
